import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';

import {
  FILTER_PARAM_PREFIX,
  FILTER_PROMPT_DEPLOYMENT_ID_KEY,
  FILTER_PROMPT_DEPLOYMENT_KEY,
  FILTER_PROMPT_DEPLOY_COUNT_KEY,
  FILTER_PROMPT_DEPLOY_ROLLBACK_KEY,
  FILTER_PROMPT_HEADER_TEXT_KEY,
  FILTER_PROMPT_QUERY_KEY,
  FILTER_PROMPT_TRIGGER_TEXT_KEY,
  FILTER_PROMPT_VALUES_KEY,
} from '@fly/shared';

import { FlyFilterByPrompt } from '../../FlyFilterByPrompt';
import {
  CursorPaginationDirection,
  CursorPaginationState,
  PaginationType,
  TableState,
} from '../TanstackTable.types';

const SEARCH_PARAM_KEY = 'search';
export const CURSOR_PARAM_KEY = 'cursor';
const CURSOR_DIRECTION_PARAM_KEY = 'cursor_direction';

const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

export type PromptDisplayLabels = {
  query?: string;
  triggerText?: string;
  headerText?: string;
};

export type UseServerTableStateConfig = {
  paginationType: PaginationType;
  searchDebounceMs?: number;
  /** When set, overrides raw f.prompt_* label params (e.g. locale-safe release-delta deep links). */
  resolvePromptDisplay?: (
    filterValues: Record<string, string | undefined>
  ) => PromptDisplayLabels | undefined;
};

/**
 * When server only returns endCursor (and hasPreviousPage), "prev" uses a client-side stack:
 * on NEXT we push the current "after" before setting the new one; on PREV we pop and use that as "after".
 */
export function useServerTableState({
  paginationType,
  searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
  resolvePromptDisplay,
}: UseServerTableStateConfig): {
  search: string | undefined;
  /** Cursor to send as "after" for the current request (next or prev). Server typically returns only endCursor; prev uses stacked cursor. */
  nextPageCursor: string | undefined;
  /** True when Prev can actually navigate (stack has entries). After refresh stack is empty so Prev should be disabled by passing prevCursor only when this is true. */
  hasPrevStack: boolean;
  /** All filter values from URL (every f.<key> param). Caller builds GraphQL vars or REST query from this. */
  filterValues: Record<string, string | undefined>;
  handleStateChange: (state: Partial<TableState>) => void;
  clearFilters: () => void;
  /** Clears all prompt-scoped f.* params (query, values, deployment, labels); use for FlyFilterByPrompt clear. */
  clearPromptFilters: () => void;
  /** True if any filter value is set. */
  hasFilterValues: boolean;
  /** Current prompt query from URL (f.prompt_query). */
  promptQuery: string | undefined;
  /** FlyFilterByPrompt trigger label from URL (f.prompt_trigger_text). */
  promptTriggerText: string | undefined;
  /** FlyFilterByPrompt dropdown header from URL (f.prompt_header_text). */
  promptHeaderText: string | undefined;
  /** True if any prompt-scoped f.* param is set (query, values, deployment, labels). */
  hasPromptFilterValues: boolean;
  /** Ready-to-render prompt filter UI (FlyFilterByPrompt); pass to TanstackTable promptFilter prop. */
  promptFilter: React.ReactNode;
  /** True while search debounce is in flight (visual input changed but `search` not yet updated). */
  isSearchPending: boolean;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const termFromUrl = searchParams.get(SEARCH_PARAM_KEY) || undefined;
  const cursorFromUrl =
    paginationType === 'cursor'
      ? searchParams.get(CURSOR_PARAM_KEY) || undefined
      : undefined;
  const cursorDirectionFromUrl =
    paginationType === 'cursor'
      ? searchParams.get(CURSOR_DIRECTION_PARAM_KEY) || undefined
      : undefined;

  const [search, setSearch] = useState<string | undefined>(termFromUrl);
  const [nextPageCursor, setNextPageCursor] = useState<string | undefined>(
    cursorFromUrl && cursorDirectionFromUrl ? cursorFromUrl : undefined
  );

  const afterStackRef = useRef<(string | undefined)[]>([]);
  const [prevStackLength, setPrevStackLength] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const [clearEpoch, setClearEpoch] = useState(0);
  const lastClearEpochRef = useRef(0);
  const [isSearchPending, setIsSearchPending] = useState(false);
  /** True after user has clicked Next/Prev; prevents sync from overwriting first page when URL lags. */
  const hasPaginatedByUserRef = useRef(false);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    };
  }, []);

  // Sync nextPageCursor from URL only when state has no cursor yet (e.g. refresh with stale first render).
  // Do not sync after user has used Prev to reach first page, or URL lag would overwrite and refetch wrong page.
  useEffect(() => {
    if (paginationType !== 'cursor') return;
    if (hasPaginatedByUserRef.current) return;
    const cursor = searchParams.get(CURSOR_PARAM_KEY) || undefined;
    const direction = searchParams.get(CURSOR_DIRECTION_PARAM_KEY) || undefined;
    if (cursor && direction && nextPageCursor === undefined) {
      setNextPageCursor(cursor);
    }
  }, [searchParams, paginationType, nextPageCursor]);

  const filterValues = useMemo(() => {
    if (clearEpoch !== lastClearEpochRef.current) {
      return {};
    }
    const out: Record<string, string | undefined> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith(FILTER_PARAM_PREFIX)) {
        out[key.slice(FILTER_PARAM_PREFIX.length)] = value || undefined;
      }
    });
    return out;
  }, [searchParams, clearEpoch]);

  useEffect(() => {
    lastClearEpochRef.current = clearEpoch;
  }, [clearEpoch]);

  const handleStateChange = useCallback(
    (state: Partial<TableState>) => {
      const searchValue = state.columnFilters?.[0]?.value as string | undefined;
      const nextSearchValue = searchValue || undefined;

      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;

      if (nextSearchValue !== search) {
        setIsSearchPending(true);
        debounceRef.current = setTimeout(() => {
          setIsSearchPending(false);
          setSearch(nextSearchValue);
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              if (nextSearchValue) {
                next.set(SEARCH_PARAM_KEY, nextSearchValue);
              } else {
                next.delete(SEARCH_PARAM_KEY);
              }
              // Preserve cursor pagination so debounced search update does not clear them
              if (paginationType === 'cursor') {
                const current = new URLSearchParams(window.location.search);
                const cursor =
                  prev.get(CURSOR_PARAM_KEY) || current.get(CURSOR_PARAM_KEY);
                const direction =
                  prev.get(CURSOR_DIRECTION_PARAM_KEY) ||
                  current.get(CURSOR_DIRECTION_PARAM_KEY);
                if (cursor) next.set(CURSOR_PARAM_KEY, cursor);
                if (direction) next.set(CURSOR_DIRECTION_PARAM_KEY, direction);
              }
              return next;
            },
            {
              replace: true,
            }
          );
          debounceRef.current = undefined;
        }, searchDebounceMs);
      } else {
        setIsSearchPending(false);
      }

      if (paginationType === 'cursor') {
        const pagination = state.pagination as
          | CursorPaginationState
          | undefined;
        if (pagination?.cursor_direction === CursorPaginationDirection.NEXT) {
          if (pagination.cursor) {
            hasPaginatedByUserRef.current = true;
            setPrevStackLength((n) => n + 1);
            setNextPageCursor((prev) => {
              afterStackRef.current = [...afterStackRef.current, prev];
              return pagination.cursor;
            });
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set(CURSOR_PARAM_KEY, pagination.cursor!);
              next.set(
                CURSOR_DIRECTION_PARAM_KEY,
                CursorPaginationDirection.NEXT
              );
              return next;
            });
          }
        } else if (
          pagination?.cursor_direction === CursorPaginationDirection.PREV
        ) {
          hasPaginatedByUserRef.current = true;
          const stack = afterStackRef.current;
          if (stack.length > 0) {
            setPrevStackLength((n) => Math.max(0, n - 1));
            const next = [...stack];
            const popped = next.pop();
            afterStackRef.current = next;
            setNextPageCursor(popped);
            setSearchParams((prev) => {
              const params = new URLSearchParams(prev);
              if (popped) {
                params.set(CURSOR_PARAM_KEY, popped);
                params.set(
                  CURSOR_DIRECTION_PARAM_KEY,
                  CursorPaginationDirection.PREV
                );
              } else {
                params.delete(CURSOR_PARAM_KEY);
                params.delete(CURSOR_DIRECTION_PARAM_KEY);
              }
              return params;
            });
          } else {
            // After refresh we have no stack (no stored "after" for previous page); go to first page
            setNextPageCursor(undefined);
            setSearchParams((prev) => {
              const params = new URLSearchParams(prev);
              params.delete(CURSOR_PARAM_KEY);
              params.delete(CURSOR_DIRECTION_PARAM_KEY);
              return params;
            });
          }
        }
      }
    },
    [paginationType, search, searchDebounceMs, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = undefined;
    setIsSearchPending(false);
    setClearEpoch((e) => e + 1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      prev.forEach((_, key) => {
        if (key.startsWith(FILTER_PARAM_PREFIX)) {
          next.delete(key);
        }
      });
      return next;
    });
  }, [setSearchParams]);

  /** Clears all prompt-scoped f.* params; used by FlyFilterByPrompt clear. */
  const clearPromptFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(`${FILTER_PARAM_PREFIX}${FILTER_PROMPT_QUERY_KEY}`);
      next.delete(`${FILTER_PARAM_PREFIX}${FILTER_PROMPT_VALUES_KEY}`);
      next.delete(`${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOYMENT_KEY}`);
      next.delete(`${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOYMENT_ID_KEY}`);
      next.delete(`${FILTER_PARAM_PREFIX}${FILTER_PROMPT_TRIGGER_TEXT_KEY}`);
      next.delete(`${FILTER_PARAM_PREFIX}${FILTER_PROMPT_HEADER_TEXT_KEY}`);
      next.delete(`${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOY_COUNT_KEY}`);
      next.delete(`${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOY_ROLLBACK_KEY}`);
      return next;
    });
  }, [setSearchParams]);

  const hasFilterValues = useMemo(
    () => Object.values(filterValues).some(Boolean),
    [filterValues]
  );

  const promptQueryValue = filterValues[FILTER_PROMPT_QUERY_KEY];
  const promptTriggerText = filterValues[FILTER_PROMPT_TRIGGER_TEXT_KEY];
  const promptHeaderText = filterValues[FILTER_PROMPT_HEADER_TEXT_KEY];
  const resolvedPromptDisplay = resolvePromptDisplay?.(filterValues);
  const promptQueryForDisplay =
    resolvedPromptDisplay?.query ?? promptQueryValue;
  const promptTriggerTextForDisplay =
    resolvedPromptDisplay?.triggerText ?? promptTriggerText;
  const promptHeaderTextForDisplay =
    resolvedPromptDisplay?.headerText ?? promptHeaderText;
  const hasPromptFilterValues = Boolean(
    promptQueryForDisplay ||
    filterValues[FILTER_PROMPT_VALUES_KEY] ||
    filterValues[FILTER_PROMPT_DEPLOYMENT_KEY] ||
    filterValues[FILTER_PROMPT_DEPLOYMENT_ID_KEY] ||
    promptTriggerTextForDisplay ||
    promptHeaderTextForDisplay ||
    filterValues[FILTER_PROMPT_DEPLOY_COUNT_KEY]
  );
  const promptFilter = useMemo(
    () =>
      hasPromptFilterValues ? (
        <FlyFilterByPrompt
          query={promptQueryForDisplay}
          clear={clearPromptFilters}
          triggerText={promptTriggerTextForDisplay}
          headerText={promptHeaderTextForDisplay}
        />
      ) : undefined,
    [
      hasPromptFilterValues,
      promptQueryForDisplay,
      promptTriggerTextForDisplay,
      promptHeaderTextForDisplay,
      clearPromptFilters,
    ]
  );

  return {
    search,
    nextPageCursor,
    hasPrevStack: prevStackLength > 0,
    filterValues,
    handleStateChange,
    clearFilters,
    clearPromptFilters,
    hasFilterValues,
    promptQuery: promptQueryForDisplay,
    promptTriggerText: promptTriggerTextForDisplay,
    promptHeaderText: promptHeaderTextForDisplay,
    hasPromptFilterValues,
    promptFilter,
    isSearchPending,
  };
}
